import { useEffect, useMemo, useState } from "react";
import { X, AlertTriangle, Info } from "lucide-react";
import { useLocale } from "@/context/LanguageContext";
import {
  ReportPeriodType,
  ReportStatus,
  ReportLocale,
  type GenerateOrgReportInput,
  type OrgReportListItemFragment,
} from "@/graphql/generated";
import {
  buildMonthOptions,
  buildQuarterOptions,
} from "../utils/periodPickers";
import { useGenerateOrgReport, useRegenerateOrgReport } from "../hooks/useReports";

interface Props {
  open: boolean;
  /**
   * Active org id. Nullable to match `useOrgContext` — the dialog renders a
   * disabled state if the user landed here mid-org-switch (extremely rare).
   */
  organizationId: string | null;
  /**
   * Already-loaded reports for the current org. Used to detect when the
   * picked period already has a report, so the dialog can swap the action
   * to "Regenerate existing".
   */
  existingReports: OrgReportListItemFragment[];
  onClose: () => void;
  onCreated: () => void;
}

// The picker uses local-time month/year (see buildMonthOptions /
// buildQuarterOptions), and the backend stores periodStart as a local-tz
// midnight that serialises to a UTC-shifted ISO string. Compare in the
// browser's local zone — matches the picker exactly, which is what the user
// sees in the dropdown.
function isSameMonth(report: OrgReportListItemFragment, month: number, year: number): boolean {
  if (report.periodType !== ReportPeriodType.Monthly) return false;
  const start = new Date(report.periodStart);
  return start.getFullYear() === year && start.getMonth() + 1 === month;
}

function isSameQuarter(
  report: OrgReportListItemFragment,
  quarter: number,
  year: number,
): boolean {
  if (report.periodType !== ReportPeriodType.Quarterly) return false;
  const start = new Date(report.periodStart);
  const reportQuarter = Math.floor(start.getMonth() / 3) + 1;
  return start.getFullYear() === year && reportQuarter === quarter;
}

/**
 * Modal for triggering a new engagement report. Native React state — the
 * portal-wide convention; react-hook-form / zod aren't introduced for
 * single-field forms like this one. Period selection is limited to
 * pre-built options (last 12 months, last 8 quarters) so we can't accept
 * malformed dates client-side.
 */
export function GenerateReportDialog({
  open,
  organizationId,
  existingReports,
  onClose,
  onCreated,
}: Props) {
  const { t, locale } = useLocale();
  // Phase 1 ships Monthly + Quarterly; CUSTOM is reserved for Phase 3.
  // Widened to ReportPeriodType so the iterator below can pass any value
  // from the enum array without an extra cast — CUSTOM stays unreachable
  // because the loop only emits Monthly and Quarterly buttons.
  const [periodType, setPeriodType] = useState<ReportPeriodType>(
    ReportPeriodType.Monthly,
  );
  const months = useMemo(() => buildMonthOptions(12, locale), [locale]);
  const quarters = useMemo(() => buildQuarterOptions(8), []);
  const [monthIdx, setMonthIdx] = useState(0);
  const [quarterIdx, setQuarterIdx] = useState(0);
  const [submissionError, setSubmissionError] = useState<string | null>(null);

  const [generate, { loading: generating }] = useGenerateOrgReport(organizationId);
  const [regenerate, { loading: regenerating }] = useRegenerateOrgReport();
  const loading = generating || regenerating;

  // Match the currently picked period against the org's reports. We accept
  // any non-FAILED status so the dialog mirrors the resolver's duplicate
  // guard exactly — FAILED rows are handled via the per-row Regenerate
  // button in the table.
  const matchedReport: OrgReportListItemFragment | null = useMemo(() => {
    const candidates = existingReports.filter(
      (r) => r.status !== ReportStatus.Failed,
    );
    if (periodType === ReportPeriodType.Monthly) {
      const pick = months[monthIdx];
      if (!pick) return null;
      return (
        candidates.find((r) => isSameMonth(r, pick.month, pick.year)) ?? null
      );
    }
    const pick = quarters[quarterIdx];
    if (!pick) return null;
    return (
      candidates.find((r) => isSameQuarter(r, pick.quarter, pick.year)) ?? null
    );
  }, [existingReports, periodType, monthIdx, quarterIdx, months, quarters]);

  const matchIsInFlight =
    matchedReport?.status === ReportStatus.Pending ||
    matchedReport?.status === ReportStatus.Generating;

  // Reset form state whenever the dialog re-opens — avoids stale error
  // banners or stale period selection if the user dismissed mid-edit.
  useEffect(() => {
    if (open) {
      setPeriodType(ReportPeriodType.Monthly);
      setMonthIdx(0);
      setQuarterIdx(0);
      setSubmissionError(null);
    }
  }, [open]);

  if (!open) return null;

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setSubmissionError(null);
    if (!organizationId) {
      setSubmissionError(t("reports.error.generic"));
      return;
    }
    if (matchIsInFlight) {
      // Already PENDING/GENERATING — submit is disabled, but guard anyway.
      return;
    }

    // Pin the report to the user's current portal locale — formats every
    // number, date, and template string in the PDF to one language.
    const reportLocale =
      locale === "de"
        ? ReportLocale.De
        : locale === "tr"
          ? ReportLocale.Tr
          : ReportLocale.En;

    try {
      if (matchedReport) {
        // Same period already has a non-failed report — regenerate it
        // instead of letting the backend silently return the existing row.
        await regenerate({ variables: { reportId: matchedReport._id } });
        onCreated();
        onClose();
        return;
      }

      const input: GenerateOrgReportInput =
        periodType === ReportPeriodType.Monthly
          ? {
              organizationId,
              periodType: ReportPeriodType.Monthly,
              locale: reportLocale,
              month: months[monthIdx].month,
              year: months[monthIdx].year,
            }
          : {
              organizationId,
              periodType: ReportPeriodType.Quarterly,
              locale: reportLocale,
              quarter: quarters[quarterIdx].quarter,
              year: quarters[quarterIdx].year,
            };
      await generate({ variables: { input } });
      onCreated();
      onClose();
    } catch (error) {
      // Translate the backend error code into a user-friendly string.
      const apolloError = error as {
        graphQLErrors?: Array<{ extensions?: { code?: string } }>;
      };
      const code = apolloError?.graphQLErrors?.[0]?.extensions?.code;
      if (code === "REPORT_RATE_LIMIT_EXCEEDED") {
        setSubmissionError(t("reports.error.rateLimit"));
      } else if (code === "VALIDATION_ERROR") {
        setSubmissionError(t("reports.error.validation"));
      } else if (code === "FORBIDDEN") {
        setSubmissionError(t("reports.error.forbidden"));
      } else {
        setSubmissionError(t("reports.error.generic"));
      }
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      onClick={onClose}
      role="presentation"
    >
      <div
        className="w-full max-w-md rounded-lg border border-border bg-card p-6 shadow-xl"
        onClick={(event) => event.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="generate-report-title"
      >
        <div className="mb-4 flex items-start justify-between">
          <h2
            id="generate-report-title"
            className="text-lg font-semibold text-foreground"
          >
            {t("reports.dialog.title")}
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="-m-1 rounded-md p-1 text-muted-foreground transition hover:text-foreground"
            aria-label={t("common.close")}
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <fieldset>
            <legend className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              {t("reports.dialog.periodTypeLabel")}
            </legend>
            <div className="mt-2 flex gap-2">
              {[ReportPeriodType.Monthly, ReportPeriodType.Quarterly].map((value) => {
                const isSelected = periodType === value;
                const labelKey =
                  value === ReportPeriodType.Monthly
                    ? "reports.dialog.periodType.monthly"
                    : "reports.dialog.periodType.quarterly";
                return (
                  <button
                    key={value}
                    type="button"
                    onClick={() => setPeriodType(value)}
                    className={`flex-1 rounded-md border px-3 py-2 text-sm font-medium transition ${
                      isSelected
                        ? "border-primary bg-primary/10 text-primary"
                        : "border-border bg-background text-foreground hover:bg-muted"
                    }`}
                    aria-pressed={isSelected}
                  >
                    {t(labelKey)}
                  </button>
                );
              })}
            </div>
          </fieldset>

          {periodType === ReportPeriodType.Monthly ? (
            <label className="block">
              <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                {t("reports.dialog.month")}
              </span>
              <select
                value={monthIdx}
                onChange={(event) => setMonthIdx(Number(event.target.value))}
                className="mt-1 block w-full rounded-md border border-border bg-background px-3 py-2 text-sm text-foreground shadow-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
              >
                {months.map((m, idx) => (
                  <option key={`${m.year}-${m.month}`} value={idx}>
                    {m.label}
                  </option>
                ))}
              </select>
            </label>
          ) : (
            <label className="block">
              <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                {t("reports.dialog.quarter")}
              </span>
              <select
                value={quarterIdx}
                onChange={(event) => setQuarterIdx(Number(event.target.value))}
                className="mt-1 block w-full rounded-md border border-border bg-background px-3 py-2 text-sm text-foreground shadow-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
              >
                {quarters.map((q, idx) => (
                  <option key={`${q.year}-Q${q.quarter}`} value={idx}>
                    {q.label}
                  </option>
                ))}
              </select>
            </label>
          )}

          {matchedReport ? (
            <div
              className={`flex items-start gap-2 rounded-md border p-3 text-sm ${
                matchIsInFlight
                  ? "border-warning/30 bg-warning/10 text-foreground"
                  : "border-primary/30 bg-primary/5 text-foreground"
              }`}
              role="status"
            >
              <Info className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
              <p>
                {matchIsInFlight
                  ? t("reports.dialog.alreadyInFlight")
                  : t("reports.dialog.alreadyExists")}
              </p>
            </div>
          ) : null}

          {submissionError ? (
            <div className="flex items-start gap-2 rounded-md border border-destructive/30 bg-destructive/10 p-3 text-sm text-destructive">
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
              <p>{submissionError}</p>
            </div>
          ) : null}

          <div className="flex justify-end gap-2 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="rounded-md border border-border bg-background px-4 py-2 text-sm font-medium text-foreground transition hover:bg-muted"
            >
              {t("common.cancel")}
            </button>
            <button
              type="submit"
              disabled={loading || matchIsInFlight}
              className="inline-flex items-center gap-2 rounded-md bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground shadow-sm transition hover:bg-primary/90 disabled:cursor-not-allowed disabled:opacity-60"
            >
              {loading
                ? t("reports.dialog.submitting")
                : matchedReport
                  ? t("reports.dialog.submitRegenerate")
                  : t("reports.dialog.submit")}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
